// Copyright 2021 Rennie Glen Software LLC
//
// Glossary
//
// Operation: one fundamental step in the imposing process like Grid,
//   Rotate, Colorbar, etc.
//
// Tool: one tool displayed in the toolbox.  It can be either a single
//   operation, or a sequence of operations, a.k.a. Multistep.
//
// Params: A struct of Params.  A Param contains backing text and maybe an error
//   message for a setting displayed to and modified by the user.  The params'
//   text is raw and may not be well formed. For example, the user may
//   have entered the text 'cheese' for the margins setting.  The margins param
//   will contain the text 'cheese' and an error message reporting that margins
//   must be a number.
//   
//   Calling await validate() on a Params struct returns a Values or throws an
//   exception if some Param contains invalid text.
//
//   Calling pack() on a Params struct returns an object containing just
//   the raw backing text for each Param.  It gets serialized to JSON and
//   stored for later unpacking.
//
//   Calling unpack() on a Params struct loads the params previously
//   serialized by pack().
//
// Values: A struct of *valid* settings that get passed to a worker thread
//   which invokes web assembly code to actually do the imposing.
//   Also known as WasmValues.
//   Values are passed from the UI to the worker thread.
//
// UIParams are passed from worker thread back to the UI.  They contain
//   information produced by the worker thread that needs to be displayed in
//   the UI.
//   Examples:
//     Layer names for the Layers tool.
//     Thumbnails for Gang tool.
//
// A list of Steps is passed to PDFWorkerPool.operate().
//   Each step contains:
//     1. values to be passed to worker & web assembly
//     2. an overlay file for Overlay or InsertPages operation; also passed
//        to worker and web assembly.
//     3. uiparams for Gang (and other tools to come).

import { newPositiveNumberParam, newNumberParam, TextParam, newPageRangeParam, 
    newNaturalNumberParam, newEnumParam, newNonEmptyTextParam, newWholeNumberParam,
    TextParamWithType, PageRangeTerms, newPowerOfTwoParam, newNonNegativeNumberParam, 
    newTextParam, ParamsArray,
    newOneHundredNumberParam
} from "./TextParam";
import { CheckboxParam } from "./CheckboxParam";
import { Operation } from "./Operation";
import { Units, UnitsBox, convert } from "./Units";
import { Param } from "./Param";
import Bugsnag from '@bugsnag/js';
import { Corner } from "./Rules";
import { toNotifiableError } from "./BugsnagHelpers";
import { toRaw } from "vue";
import { OpTool} from "./ToolKind";
import { Toggle, ToggleLayers } from "./ToggleLayers";

export enum PageOrder {
    StepAndRepeat = "stepAndRepeat",
    Sequential = "sequential",
    Stack = "stack",
}

export enum WorkStyle {
    sheetwise = "sheetwise",
    workAndTurn = "workAndTurn",
    workAndTumble = "workAndTumble",
    perfecting = "perfecting"
}

export function pageOrderName(t: (key: string) => string, order: PageOrder): string {
    switch (order) {
        case PageOrder.Sequential: return t("pageOrder.sequential").toString();
        case PageOrder.Stack: return t("pageOrder.stack").toString();
        case PageOrder.StepAndRepeat: return t("pageOrder.stepAndRepeat").toString();
    }    
}

export enum Bleeds {
    PullFromDoc = "pullFromDoc",
    NoBleeds = "noBleeds",
    Fixed = "fixed"
}

export enum Direction {
    LeftToRight = "leftToRight",
    RightToLeft = "rightToLeft"
}

export enum RowOrientation {
    HeadToFoot = "headToFoot",
    HeadToHead = "headToHead",
}

export enum BlendMode {
    Normal = "normal",
    Multiply = "multiply",
    Screen = "screen",
    Overlay = "overlay",
    Darken = "darken",
    Lighten = "lighten",
    ColorDodge = "colorDodge",
    ColorBurn = "colorBurn",
    HardLight = "hardLight",
    SoftLight = "softLight",
    Difference = "difference",
    Exclusion = "exclusion"
}

export enum PaddingPosition {
    TopLeft = "topLeft",
    TopCenter = "topCenter",
    TopRight = "topRight",
    MiddleLeft = "middleLeft",
    MiddleCenter = "middleCenter",
    MiddleRight = "middleRight",
    BottomLeft = "bottomLeft",
    BottomCenter = "bottomCenter",
    BottomRight = "bottomRight"
}

export enum NestingAlgorithm {
    Tetris = "tetris",
}

// For header and footer text.
export enum FontName {
    TimesRoman = 'Times-Roman',
    Helvetica = 'Helvetica',
    Courier = 'Courier',
    Code39 = 'Code39',
    Code39WithText = "Code39-WithText"
}

// For header and footer location.
export enum Row {
    Bottom = 'bottom',
    Middle = 'middle',
    Top = 'top'
}

export enum Column {
    Left = 'left',
    Center = 'center',
    Right = 'right'
}

export enum ColorBarLocation {
    Top = 'top',
    Bottom = 'bottom',
    Left = 'left',
    Right = 'right'
}

export enum CalendarInputLayout {
    HalfSheet = 'halfSheet',
    FullSheet = 'fullSheet'
}

export enum MediaFormat {
    Sheets = 'sheets',
    Roll = 'roll'
}

export enum CutterMarkShape {
    Circle = 'circle',
    Square = 'square',
    L = 'l'
}

export enum KeyMarkLocation {
    NorthNorthWest = 'northNorthWest',
    NorthNorthEast = 'northNorthEast',
    EastNorthEast = 'eastNorthEast',
    EastSouthEast = 'eastSouthEast',
    SouthSouthEast = 'southSouthEast',
    SouthSouthWest = 'southSouthWest',
    WestSouthWest = 'westSouthWest',
    WestNorthWest = 'westNorthWest'
}

export enum CutterMarkLocations {
    CornersOnly = 'cornersOnly',
    CornersAndEdges = 'cornersAndEdges'
}

export enum CutterMarkSide {
    Inside = "inside",
    Outside = "outside"
}

export enum CutterMarkBox {
    Art = "art",
    Bleed = "bleed",
    Crop = "crop",
    Media = "media",
    Trim = "trim"
}

export enum CutterMarkOrientation {
    In = 'in',
    Out = 'out',
    NW = 'nw',
    NE = 'ne',
    SW = 'sw',
    SE = 'se'
}

export enum ColorMode {
    RGB = "rgb",
    CMYK = "cmyk"
}

type Scalar = (TextParam<number> | ParamsArray<number, TextParam<number>>);
type Scalars = Scalar[];

export interface AbstractParams {
    readonly kind: string;
    /// A list of fields whose units are inches, mms, points.  These fields
    /// get updated when the user switches units.
    readonly scalars: Scalars;
}

export type ToolParams = ResizeParams | RotateParams | GridParams | MonkeyParams
| CropParams | ShuffleParams | BookParams | CardsParams | HeaderFooterParams
| FlipParams | NupBookParams | SplitParams | OverlayParams | NudgeParams
| ColorBarParams | NestParams | CalendarParams | InsertPagesParams
| GangParams | CutterMarksParams | ToggleLayersParams | ExpertParams
| MakeBleedsParams | MaskingTapeParams;

export type AnyParams = ToolParams | PaperSizeParams;

export interface ParamsWithPageRange {
    readonly pageRange: TextParam<string>;
}

export function localAll(terms: PageRangeTerms) {
    const found = terms.find(([ , value]) => value == "all");
    return found ? found[0] : "all";
}

export interface ICropParams {
  readonly cropMarks: CheckboxParam;
  readonly centerMarks: CheckboxParam;
  readonly lineLength: TextParamWithType<number>;
  readonly lineThickness: TextParamWithType<number>;
  readonly lineDistance: TextParamWithType<number>;
  readonly fourColorBlack: CheckboxParam;
  readonly whiteBorder: CheckboxParam;
  readonly marksInGutters?: CheckboxParam;
}

export interface IBleedsParams {
    readonly bleeds: TextParamWithType<string>;
    readonly fixedBleedLeft: TextParamWithType<number>;
    readonly fixedBleedTop: TextParamWithType<number>;
}

function defaultExpertPageNumbers(): ParamsArray<number, TextParamWithType<number>> {
    const a = new ParamsArray<number, TextParamWithType<number>>(
        () => newNonNegativeNumberParam(""));
    for (let n of [4, 1, 2, 3]) {
        a.array.push(newNonNegativeNumberParam(String(n)));
    }
    return a;
}

function newNumericParamsArray(): ParamsArray<number, TextParamWithType<number>> {
    return new ParamsArray(() => newNumberParam("0"));
}

export class ExpertParams implements AbstractParams, ICropParams, IBleedsParams {
    readonly kind = Operation.Expert;

    readonly sheetCount = newNaturalNumberParam("1");
    readonly columnCount = newNaturalNumberParam("2");
    readonly rowCount = newNaturalNumberParam("2");
    readonly pageCreep = newNumberParam("0");
    readonly pageCreepUpDown = newNumericParamsArray();
    readonly pageCreepLeftRight = newNumericParamsArray();
    readonly rowGutterWidths = newNumericParamsArray();
    readonly colGutterWidths = newNumericParamsArray();
    readonly doubleSided = new CheckboxParam(true);

    readonly pageNumbers = defaultExpertPageNumbers();
    readonly pageRotations = newNumericParamsArray();

    readonly cropMarks = new CheckboxParam(false);
    readonly marksInGutters = new CheckboxParam(true);
    readonly centerMarks = new CheckboxParam(false);
    readonly lineLength = newPositiveNumberParam("31");
    readonly lineThickness = newPositiveNumberParam("1");
    readonly lineDistance = newNumberParam("10");
    readonly fourColorBlack = new CheckboxParam(false);
    readonly whiteBorder = new CheckboxParam(false);

    readonly bleeds = newEnumParam(Bleeds);
    readonly fixedBleedLeft = newNumberParam("14.4");
    readonly fixedBleedTop = newNumberParam("14.4");
    readonly repeat = new CheckboxParam(false);
    readonly copies = newNaturalNumberParam("1");
    
    get scalars(): Scalars {
        return [this.pageCreep, this.rowGutterWidths, this.colGutterWidths,
            this.lineLength, this.lineDistance, this.lineThickness,
            this.fixedBleedLeft, this.fixedBleedTop
        ];
    }
}

export enum ResizeMethod {
    Percentage = "percentage",
    PaperSize = "paperSize"
}

export class ResizeParams implements AbstractParams {
    readonly kind = Operation.Resize;

    constructor(terms: PageRangeTerms) {
        this.pageRange = newPageRangeParam(localAll(terms), terms);
    }

    readonly paperWidth = newPositiveNumberParam("612");
    readonly paperHeight = newPositiveNumberParam("792");
    readonly preserveAspectRatio = new CheckboxParam(true);
    readonly pageRange: TextParamWithType<string>;
    readonly bleeds = newNumberParam("0");
    readonly method = newEnumParam(ResizeMethod, ResizeMethod.PaperSize);
    readonly percentage = newPositiveNumberParam("50");

    get scalars(): Scalars {
        return [this.paperWidth, this.paperHeight, this.bleeds];
    }
}

export class CropParams implements AbstractParams {
    readonly kind = Operation.Crop;

    constructor(terms: PageRangeTerms) {
        this.pageRange = newPageRangeParam(localAll(terms), terms);
    }

    readonly top = newNumberParam("72");
    readonly bottom = newNumberParam("72");
    readonly left = newNumberParam("72");
    readonly right = newNumberParam("72");
    readonly pageRange: TextParamWithType<string>;

    get scalars(): Scalars {
        return [this.top, this.bottom, this.left, this.right];
    }
}

// Params for the paper sizes dialog only.  Never stored.
// The main reason this exists is to reuse the unit conversion logic.
export class PaperSizeParams implements AbstractParams {
    readonly kind = "PaperSizeParams";
    readonly width = newPositiveNumberParam("612");
    readonly height = newPositiveNumberParam("792");
    readonly name = newNonEmptyTextParam("name");

    get scalars(): Scalars {
        return [this.width, this.height];
    }
}

export const kindSettingsParams = "SettingsParams";
export class SettingsParams implements AbstractParams {
    readonly kind = kindSettingsParams;
    readonly ruleSpacing = newPositiveNumberParam("18");  // Quarter inch.
    readonly origin = newEnumParam(Corner);
    readonly appendFileNames = new CheckboxParam(true);
    readonly appendCustomNames = new CheckboxParam(false);
    readonly customSuffix = newTextParam("");
    readonly showCLIbutton = new CheckboxParam(false);
    readonly scalars = [this.ruleSpacing];
    readonly displayCutBlocks = new CheckboxParam(false);
    readonly cutBlockColor = newTextParam("#FF00FF");
}

export class RotateParams implements AbstractParams {
    readonly kind = Operation.Rotate;

    constructor(terms: PageRangeTerms) {
        this.pageRange = newPageRangeParam(localAll(terms), terms);
    }

    readonly degrees = newNumberParam("90");
    readonly pageRange: TextParamWithType<string>;

    get scalars(): Scalars { return []; }
}

export class ShuffleParams implements AbstractParams {
    readonly kind = Operation.Shuffle;

    constructor(terms: PageRangeTerms) {
        this.pageRange = newPageRangeParam(localAll(terms), terms);
    }

    readonly pageRange: TextParamWithType<string>;

    get scalars(): Scalars { return []; }
}

export enum TextRotation {
    Degrees0 = "0",
    Degrees90 = "90",
    Degrees180 = "180",
    Degrees270 = "270"
}

export enum FontSource {
    Builtin = "builtin",
    Custom = "custom"
}

export class HeaderFooterParams implements AbstractParams {
    readonly kind = Operation.HeaderFooter;

    constructor(terms: PageRangeTerms) {
        this.pageRange = newPageRangeParam(localAll(terms), terms);
    }
    
    readonly text = newNonEmptyTextParam("[file-name] page [page-number] of [page-count]");
    readonly fontName = newEnumParam(FontName);
    readonly fontSizeInPoints = newPositiveNumberParam("12");
    readonly row = newEnumParam(Row);
    readonly column = newEnumParam(Column);
    readonly alternateSides = new CheckboxParam(false);
    readonly margins = newNumberParam("18");
    readonly leftMargin = newNumberParam("18");
    readonly topMargin = newNumberParam("18");
    readonly pageRange: TextParamWithType<string>;
    readonly headerLayer = newTextParam("");
    readonly rotateText = newEnumParam(TextRotation);
    readonly customFontPath = newTextParam("");
    readonly customFontHash = newTextParam("");
    readonly fontSource = newEnumParam(FontSource);

    get scalars(): Scalars { return [this.margins, this.leftMargin, this.topMargin]; }
}

export class ColorBarParams implements AbstractParams {
    readonly kind = Operation.ColorBar;

    constructor(terms: PageRangeTerms) {
        this.pageRange = newPageRangeParam(localAll(terms), terms);
    }

    readonly pageRange: TextParamWithType<string>;
    readonly cmyk = new CheckboxParam(true);
    readonly spot = new CheckboxParam(true);
    readonly location = newEnumParam(ColorBarLocation);
    readonly width = newPositiveNumberParam("18");
    readonly longMargin = newNonNegativeNumberParam("18");
    readonly shortMargin = newNonNegativeNumberParam("18");
    readonly fullToneRectangles = new CheckboxParam(true);    
    readonly halfToneRectangles = new CheckboxParam(true);
    readonly fullHalfToneTriangles = new CheckboxParam(true);
    readonly crossHairs = new CheckboxParam(true);
    readonly hexagons = new CheckboxParam(true);
    readonly starBursts = new CheckboxParam(true);
    readonly repeat = new CheckboxParam(true);
    readonly colorbarLayer = newTextParam("");

    get scalars(): Scalars { return [this.width, this.longMargin, this.shortMargin]; }
}

export class CutterMarksParams implements AbstractParams {
    readonly kind = Operation.CutterMarks;

    readonly shape = newEnumParam(CutterMarkShape);
    readonly size = newPositiveNumberParam("18");
    readonly thickness = newPositiveNumberParam("4");
    readonly orientation = newEnumParam(CutterMarkOrientation, CutterMarkOrientation.In);
    readonly oneMarkOnly = new CheckboxParam(false);
    readonly margin = newNonNegativeNumberParam("18");
    readonly locations = newEnumParam(CutterMarkLocations);
    readonly spacing = newPositiveNumberParam("720");
    readonly layer = newTextParam("Regmark");
    readonly pageRange: TextParamWithType<string>;
    readonly keyMarkLocation = newEnumParam(KeyMarkLocation, KeyMarkLocation.NorthNorthWest);
    readonly keyMarkDistance = newNonNegativeNumberParam("144");

    readonly dielines = new CheckboxParam(false);
    readonly dielinesColor = newTextParam("#0aFF30");
    readonly dielinesColorMode = newEnumParam(ColorMode, ColorMode.RGB);
    readonly dielinesCyan = newNonNegativeNumberParam("100");
    readonly dielinesMagenta = newNonNegativeNumberParam("0");
    readonly dielinesYellow = newNonNegativeNumberParam("100");
    readonly dielinesBlack = newNonNegativeNumberParam("0");
    readonly dielinesLayer = newNonEmptyTextParam("Thru-Cut");
    readonly dielinesCombineCommonCuts = new CheckboxParam(false);
    readonly dielinesCommonCutsOvershoot = newNumberParam("0");
    readonly removeArtwork = new CheckboxParam(false);
    readonly side = newEnumParam(CutterMarkSide, CutterMarkSide.Inside);
    readonly box = newEnumParam(CutterMarkBox, CutterMarkBox.Media);

    constructor(terms: PageRangeTerms) {
        this.pageRange = newPageRangeParam(localAll(terms), terms);
    }

    get scalars(): Scalars { return [
        this.margin, this.size, this.spacing, this.keyMarkDistance, this.thickness, this.dielinesCommonCutsOvershoot
    ]; }
}

function newOneHundredParamsArray(): ParamsArray<number, TextParamWithType<number>> {
    return new ParamsArray(() => newOneHundredNumberParam("0"))
}

export class MaskingTapeParams implements AbstractParams {
    readonly kind = Operation.MaskingTape;

    readonly left = newNumericParamsArray();
    readonly bottom = newNumericParamsArray();
    readonly right = newNumericParamsArray();
    readonly top = newNumericParamsArray();
    readonly cyan = newOneHundredParamsArray();
    readonly magenta = newOneHundredParamsArray();
    readonly yellow = newOneHundredParamsArray();
    readonly black = newOneHundredParamsArray();
    readonly pageRange: ParamsArray<string, TextParamWithType<string>>;

    constructor(terms: PageRangeTerms) {
        this.pageRange = new ParamsArray(() => newPageRangeParam("", terms));
    }

    get scalars(): Scalars { return [this.left, this.bottom, this.right, this.top]; }
}

export class SplitParams implements AbstractParams {
    readonly kind = Operation.Split;

    readonly chunkSize = newNaturalNumberParam("20");

    get scalars(): Scalars { return []; }
}

export enum MakeBleedsMethod {
    Enlarge = "enlarge",
    Mirror = "mirror"
}

export class MakeBleedsParams implements AbstractParams {
    readonly kind = Operation.MakeBleeds;

    constructor(terms: PageRangeTerms) {
        this.pageRange = newPageRangeParam(localAll(terms), terms);
    }

    readonly pageRange: TextParamWithType<string>;
    readonly method = newEnumParam(MakeBleedsMethod, MakeBleedsMethod.Mirror);
    readonly bleedWidth = newNumberParam("18");
    readonly bleedInset = newNumberParam("0");

    get scalars(): Scalars { return [this.bleedWidth, this.bleedInset]; }
}

export class FlipParams implements AbstractParams {
    readonly kind = Operation.Flip;

    constructor(terms: PageRangeTerms) {
        this.pageRange = newPageRangeParam(localAll(terms), terms);
    }

    readonly pageRange: TextParamWithType<string>;

    get scalars(): Scalars { return []; }
}

export class OverlayParams implements AbstractParams {
    readonly kind = Operation.Overlay;

    constructor(terms: PageRangeTerms) {
        this.pageRange = newPageRangeParam(localAll(terms), terms);
    }

    readonly filePath = newNonEmptyTextParam("");
    readonly fileHash = newTextParam("");
    readonly leftPadding = newNumberParam("0");
    readonly topPadding = newNumberParam("0");
    readonly blendMode = newEnumParam(BlendMode);
    readonly repeat = new CheckboxParam();
    readonly opacityPercent = newNumberParam("100");
    readonly paddingPosition = newEnumParam(PaddingPosition);
    readonly pageRange: TextParamWithType<string>;

    get scalars(): Scalars { return [this.leftPadding, this.topPadding]; }
}

export class InsertPagesParams implements AbstractParams, ParamsWithPageRange {
    readonly kind = Operation.InsertPages;

    constructor(terms: PageRangeTerms = []) {
        this.pageRange = newPageRangeParam(localAll(terms), terms);
    }

    readonly filePath = newNonEmptyTextParam("");
    readonly fileHash = newTextParam("");
    readonly insertBeforePage = newNaturalNumberParam("1");
    readonly repeat = new CheckboxParam(false);
    readonly repeatPageNum = newNaturalNumberParam("1");
    readonly pageRange: TextParamWithType<string>;

    get scalars(): Scalars { return []; }
}

export type Dir = "rtl" | "ltr";

/** Common Params for Grid, Booklet, Cards, and Monkey */
export class ImposeParams implements ICropParams, IBleedsParams {
    readonly paperWidth = newPositiveNumberParam("612");
    readonly paperHeight = newPositiveNumberParam("792");

    readonly leftMargin = newNumberParam("14.4");
    readonly topMargin = newNumberParam("14.4");
    readonly verticalGutterWidth = newNumberParam("0");
    readonly horizontalGutterWidth = newNumberParam("0");
    readonly center = new CheckboxParam(false);

    readonly pageOrder = newEnumParam(PageOrder);
    readonly doubleSided = new CheckboxParam(false);
    readonly repeat = newNaturalNumberParam("1");
    // Up until version 6.152, pageOrder = 'stack' and repeat > 1
    // yielded the wrong page order.  However, fifty customers had saved
    // steps with that wrong page order.  Simply fixing the page order would
    // break those customers.  To avoid breaking customers, Params with
    // newStackOrder missing or false use the old page order.
    readonly newStackOrder = new CheckboxParam(true);

    readonly cropMarks = new CheckboxParam(false);
    readonly marksInGutters = new CheckboxParam(true);
    readonly centerMarks = new CheckboxParam(false);
    readonly lineLength = newPositiveNumberParam("31");
    readonly lineThickness = newPositiveNumberParam("1");
    readonly lineDistance = newNumberParam("10");
    readonly fourColorBlack = new CheckboxParam(false);
    readonly whiteBorder = new CheckboxParam(false);

    readonly bleeds = newEnumParam(Bleeds);
    readonly fixedBleedLeft = newNumberParam("14.4");
    readonly fixedBleedTop = newNumberParam("14.4");

    readonly direction = newEnumParam(Direction);
    readonly rowOrientation = newEnumParam(RowOrientation);

    constructor(dir: Dir) {
        const direction = "rtl" == dir ? Direction.RightToLeft : Direction.LeftToRight;
        this.direction.text = direction;
    }

    get scalars(): Scalars {
        return [this.paperWidth, this.paperHeight, this.leftMargin, this.topMargin,
        this.verticalGutterWidth, this.horizontalGutterWidth,
        this.lineLength, this.lineThickness, this.lineDistance,
        this.fixedBleedLeft, this.fixedBleedTop];
    }
}

/** Common params for Grid and Cards */
class Nup extends ImposeParams {
    readonly columns = newNaturalNumberParam("2");
    readonly rows = newNaturalNumberParam("2");
    readonly preserveAspectRatio = new CheckboxParam(true);
}
export class GridParams extends Nup implements AbstractParams {
    readonly kind = Operation.Grid;
    readonly scale = new CheckboxParam(true);
}
export class MonkeyParams extends ImposeParams implements AbstractParams {
    readonly kind = Operation.Monkey;
}
export class CardsParams extends Nup implements AbstractParams {
    readonly kind = Operation.Cards;
    readonly scale = new CheckboxParam(false);
    constructor(dir: Dir) {
        super(dir);
        this.center.checked = true;
    }
}

class CommonBookParams implements ICropParams, IBleedsParams {
    readonly leftMargin = newNumberParam("14.4");
    readonly topMargin = newNumberParam("14.4");

    readonly cropMarks = new CheckboxParam(false);
    readonly centerMarks = new CheckboxParam(false);
    readonly lineLength = newPositiveNumberParam("31");
    readonly lineThickness = newPositiveNumberParam("1");
    readonly lineDistance = newNumberParam("10");
    readonly fourColorBlack = new CheckboxParam(false);
    readonly whiteBorder = new CheckboxParam(false);

    readonly bleeds = newEnumParam(Bleeds);
    readonly fixedBleedLeft = newNumberParam("14.4");
    readonly fixedBleedTop = newNumberParam("14.4");

    readonly direction = newEnumParam(Direction);

    constructor(dir: Dir) {
        const direction = "rtl" == dir ? Direction.RightToLeft : Direction.LeftToRight;
        this.direction.text = direction;
    }

    get scalars(): Scalars {
        return [this.leftMargin, this.topMargin,
            this.lineLength, this.lineThickness, this.lineDistance,
            this.fixedBleedLeft, this.fixedBleedTop];
    }
}

class _BookParams extends CommonBookParams {
    readonly paperHeight = newPositiveNumberParam("612");
    readonly paperWidth = newPositiveNumberParam("792");

    readonly verticalGutterWidth = newNumberParam("14.4");
    readonly pageCreep = newNumberParam("0");
    readonly center = new CheckboxParam(false);

    readonly scale = new CheckboxParam(true);
    readonly preserveAspectRatio = new CheckboxParam(true);
    readonly sheetsPerSaddle = newWholeNumberParam("");
    readonly fillLastSaddle = new CheckboxParam(false);

    readonly rotateBooklet = new CheckboxParam(true);
    readonly creepDir = newEnumParam(CreepDirection, CreepDirection.Outward);
    
    get scalars(): Scalars {
        return [...super.scalars, this.paperWidth, this.paperHeight,
            this.verticalGutterWidth, this.pageCreep];
    }
}

export class BookParams extends _BookParams implements AbstractParams {
    readonly kind = Operation.Book;
}

export enum Binding {
    Perfect = "perfect",
    NestedSaddle = "nested"
}

export enum CreepDirection {
    Inward = "inward",
    Outward = "outward"
}

export class NupBookParams extends CommonBookParams implements AbstractParams {
    readonly kind = Operation.NupBook;

    readonly bindingGutterWidth = newNumberParam("0");
    readonly bindingGutterCreep = newNumberParam("0");
    readonly otherGutterWidth = newNumberParam("0");
    readonly otherGutterCreep = newNumberParam("0");
    readonly creepDir = newEnumParam(CreepDirection);

    readonly nup = newPowerOfTwoParam("4");
    readonly binding = newEnumParam(Binding);

    get scalars(): Scalars {
        return [this.bindingGutterCreep, this.bindingGutterWidth,
            this.otherGutterCreep, this.otherGutterWidth,
            ...super.scalars];
    }
}

export type NudgeAction = { kind: "rotate"; degrees: number}
    | { kind: "translate"; x: number; y: number };

export interface Nudge {
    action: NudgeAction;
    pageRange: string;
}

export class NudgeParam implements Param<Nudge[]> {
    loadValid(obj: any): void {
        this.nudges = obj;
    }

    copyFrom(param: Param<Nudge[]>): void {
        const thisType = typeof(this);
        const thatType = typeof(param);
        const that = param as NudgeParam;
        if (thisType === thatType) {
            this.nudges = JSON.parse(JSON.stringify(that.nudges));
        } else {
            throw new Error(`Can't copy from ${thatType} to ${thisType}.`);
        }
    }
    nudges: Nudge[] = [];
    // Validation happens in the UI before being added to the nudge list.
    validate() { return Promise.resolve(this.nudges.map(toRaw)); }
    pack() { return this.nudges; }
    unpack(value: Nudge[]) { this.nudges = value; }
}

// Nudge params is very unlike the other Params, because the user constructs
// multiple steps with the UI.  The live text being edited for the page range
// and rotate and translate increments are recorded in these Params.
export class NudgeParams implements AbstractParams {
    readonly kind = Operation.Nudge;
    readonly deltaDegrees = newNumberParam("2");
    readonly deltaPosition = newNumberParam("14.4");
    constructor(terms: PageRangeTerms) {
      this.pageRange = newPageRangeParam(localAll(terms), terms);
    }
    readonly pageRange: TextParamWithType<string>;
    readonly scalars = [this.deltaPosition];
    nudges: NudgeParam = new NudgeParam();
}

export enum Repeat {
    FillSheet = "fillSheet",
    CopyCount = "copyCount"
}

export enum NestingStrategy {
    All = "All",
    TopMass = "TopMass",
    Rectangles = "Rectangles",
    MinWaste = "MinWaste",
    TopMassFromRight = "TopMassFromRight",
    MinWasteFromRight = "MinWasteFromRight",
}


export class NestParams {
    readonly kind = Operation.Nest;
    readonly paperHeight = newPositiveNumberParam("612");
    readonly paperWidth = newPositiveNumberParam("792");
    readonly rollWidth = newPositiveNumberParam("792");
    readonly mediaFormat = newEnumParam(MediaFormat);

    readonly leftMargin = newNumberParam("14.4");
    readonly topMargin = newNumberParam("14.4");
    readonly rightMargin = newNumberParam("14.4");
    readonly bottomMargin = newNumberParam("14.4");
    readonly padding = newNonNegativeNumberParam("0");

    readonly pixelsPerPoint = newPositiveNumberParam("1");
    readonly difficulty = newNaturalNumberParam("1");
    /// Always tetris for now.  There may be new algorithms in the future.
    readonly algorithm = newEnumParam(NestingAlgorithm);
    readonly whiteIsTransparent = new CheckboxParam(false);

    readonly repeat = newEnumParam(Repeat);
    readonly copyCount = newNaturalNumberParam("20");
    readonly rotations = newNaturalNumberParam("4");
    /// Only used in development builds.
    readonly nestingStrategy = newEnumParam(NestingStrategy);

    get scalars(): Scalars {
        return [this.paperHeight, this.paperWidth, this.rollWidth,
            this.leftMargin, this.rightMargin,
            this.topMargin, this.bottomMargin, this.padding];
    }
}

export class GangParams implements AbstractParams, ICropParams, IBleedsParams {
    readonly kind = Operation.Gang;
    readonly leftMargin = newNumberParam("14.4");
    readonly topMargin = newNumberParam("14.4");
    readonly rightMargin = newNumberParam("14.4");
    readonly bottomMargin = newNumberParam("14.4");
    readonly gutterWidth = newNonNegativeNumberParam("14.4");

    readonly paperHeight = newPositiveNumberParam("612");
    readonly paperWidth = newPositiveNumberParam("792");
    readonly workStyle = newEnumParam(WorkStyle);

    readonly cropMarks = new CheckboxParam(false);
    readonly marksInGutters = new CheckboxParam(true);
    readonly centerMarks = new CheckboxParam(false);
    readonly lineLength = newPositiveNumberParam("31");
    readonly lineThickness = newPositiveNumberParam("1");
    readonly lineDistance = newNumberParam("10");
    readonly fourColorBlack = new CheckboxParam(false);
    readonly whiteBorder = new CheckboxParam(false);

    readonly bleeds = newEnumParam(Bleeds);
    readonly fixedBleedLeft = newNumberParam("14.4");
    readonly fixedBleedTop = newNumberParam("14.4");

    // Spoilage
    // See https://docs.google.com/document/d/1BUqN_50GSVCiwZw0hYpno9bVBjLk9ouoqSbgliZ45Is/edit?usp=sharing
    readonly makeReadySheetCount = newNonNegativeNumberParam("0");
    readonly runningSpoilagePercent = newNonNegativeNumberParam("0");

    /// The number entered by the user.  How many counts of each item
    /// they want.
    readonly itemCounts = new ParamsArray(() => newWholeNumberParam("100"));
    readonly itemPadding = new ParamsArray<number, TextParamWithType<number>>(
        () => newNonNegativeNumberParam("0"));
    readonly itemsCanRotate = new ParamsArray<boolean, CheckboxParam>(
        () => new CheckboxParam(false));

    get scalars(): Scalars {
        return [this.paperHeight, this.paperWidth,
            this.leftMargin, this.topMargin, this.rightMargin, this.bottomMargin,
            this.gutterWidth,
            this.lineLength, this.lineThickness, this.lineDistance,
            this.fixedBleedLeft, this.fixedBleedTop,
            this.itemPadding];
    }
} 

export class CalendarParams extends _BookParams implements AbstractParams{
    readonly kind = Operation.Calendar;

    // checkbox for rotating the back cover page 180 degrees, checked by default
    readonly rotateBackCover = new CheckboxParam(true);
    readonly inputLayout = newEnumParam(CalendarInputLayout);

    get scalars(): Scalars {
        return [...super.scalars];
    }
}

export class ToggleLayersParam implements Param<[string, Toggle][]> {
    layers: ToggleLayers = {};
    validate() { return Promise.resolve(Object.entries(toRaw(this.layers))); }
    loadValid(entries: any): void { this.layers =  Object.fromEntries(entries) }
    pack() { return toRaw(this.layers); }
    unpack(value: ToggleLayers) { this.layers = value; }
    copyFrom(param: Param<[string, Toggle][]>): void {
        const thisType = typeof(this);
        const thatType = typeof(param);
        const that = param as ToggleLayersParam;
        if (thisType === thatType) {
            this.layers = {...that.layers};
        } else {
            throw new Error(`Can't copy from ${thatType} to ${thisType}.`);
        }
    }
}

export class ToggleLayersParams implements AbstractParams {
    readonly kind = Operation.ToggleLayers;

    readonly layers = new ToggleLayersParam;

    get scalars(): Scalars { return []; }
}

/// Validates all the parameters, then converts them to OpValues so they can
/// be passed to the Rust code.
export async function collectValues(params: AbstractParams, fromUnits: Units) {
    const result: {kind: Operation} & Record<string, any> = { kind: Operation.Shuffle};
    const scalarSet = new Set<Scalar>(params.scalars);
    for (const [name, param] of Object.entries(params)) {
        if ("kind" == name) {
            // kind is a string.  Just copy it over.
            result[name] = param;
        } else if ("scalars" == name) {
            // don't copy the list of scalars.
        } else if (scalarSet.has(param)) {
            // It's a scalar.  Convert to points.
            result[name] = convert(await param.validate(), fromUnits, Units.POINTS);
        } else {
            try {
                result[name] = await param.validate();
            } catch (e) {
                console.error("failed to validate", name, param, e);
                throw e;
            }
        }
    }
    return result;
}

export function paramsFromValues(values: Record<string, any>,
    terms: PageRangeTerms, dir: Dir): ToolParams
{
    const kind = values.kind as Operation;
    const params = newParams(kind, terms, dir);
    if (!params) {
        throw new Error(`Unknown kind ${kind} in values ${values}`);
    }
    for (const [name, param] of Object.entries(params)) {
        if ("kind" == name) {
            // kind is a string.
        } else if ("scalars" == name) {
            // don't copy the list of scalars.
        } else  {
            const value = values[name];
            if (value !== undefined) {
                try {
                    (param as Param<any>).loadValid(value);
                } catch (cause) {
                    throw new Error(`While loading ${name}`, { cause });
                }
            }
        }
    }
    return params;
}

export function convertParams(params: AbstractParams, oldUnits: Units,
    newUnits: Units): void
{
    for (const param of params.scalars) {
        if (param instanceof ParamsArray) {
            param.array.forEach(convertParam);
        } else {
            convertParam(param);
        }
    }

    function convertParam(param: TextParam<number>) {
        const n = Number(param.text.trim());
        if (!isNaN(n)) {
            const newText = convert(n, oldUnits, newUnits).toString();
            param.text = newText;
        }
    }
}

export function copyParams<P extends AbstractParams>(to: P, from: P): P {
    for (const [name, param] of Object.entries(from)) {
        if ("kind" == name) {
            // Kinds already match.
        } else if ("scalars" == name) {
            // Don't copy the list of scalars.
        } else {
            const f = param as Param<any>;
            const t = to[name as keyof P] as Param<any>;
            t.copyFrom(f);
        }
    }
    return to;
}

// Pack the parameters into a json object to be serialized and written to localStorage.
export function pack(params: AbstractParams, units: Units) {
    const result: Record<string, any> = { units: units };
    for (const [name, param] of Object.entries(params)) {
        if ("kind" == name) {
            // kind is a string.  Just copy it over.
            result[name] = param;
        } else if ("scalars" == name) {
            // don't copy the list of scalars.
        } else {
            result[name] = param.pack();
        }
    }
    return result;
}

// Unpack the parameters from a json object.
export function unpackParams(params: AnyParams, units: Units,
    record: Record<string, any>): void
{
    const rparams = params as Record<string, any>;
    for (const [name, value] of Object.entries(record)) {
        if ("units" == name) {
            // Skip units.  We convert later.
        } else if ("kind" == name) {
            // Skip.  Kinds match already.
        } else {
            const param: Param<any> | undefined = rparams[name];
            // There's a chance it's a new param stored by a newer version of
            // PDF Snake, so it won't be in rparams.
            param?.unpack(value);
        }
    }
    
    // If top and left margins are empty, use the existing margins value
    if (params.kind === Operation.HeaderFooter) {
        if (!record.topMargin && !record.leftMargin && record.margins) {
            params.topMargin.unpack(record.margins);
            params.leftMargin.unpack(record.margins);
        }
    }
    
    if (record.units) {
        convertParams(params, record.units, units);
    }
}

export function unpackSteps(packedSteps: Record<string, any>[],
    units: Units, terms: PageRangeTerms, dir: Dir): ToolParams[]
{
  const paramsList: ToolParams[] = [];
  for (const step of packedSteps) {
    const params = newParams(step.kind, terms, dir);
    // When loading steps, preserve the original behavior of the stack order.
    if ("newStackOrder" in params) {
      params.newStackOrder.checked = false;
    }
    try {
      unpackParams(params, units, step);
    } catch (e) {
      // Catch the error so the user can use the default values if we were
      // unable to unpack whatever values they had stored.
      Bugsnag.notify(toNotifiableError(e));
    }
    paramsList.push(params);
  }
  return paramsList;
}


export interface PageDimensions {
    widthPts: number;
    heightPts: number;
}

export function newParams(op: Operation, terms: PageRangeTerms, dir: Dir): ToolParams {
    switch (op) {
        case Operation.Resize: return new ResizeParams(terms);
        case Operation.Rotate: return new RotateParams(terms);
        case Operation.Grid: return new GridParams(dir);
        case Operation.Monkey: return new MonkeyParams(dir);
        case Operation.Crop: return new CropParams(terms);
        case Operation.Shuffle: return new ShuffleParams(terms);
        case Operation.Book: return new BookParams(dir);
        case Operation.NupBook: return new NupBookParams(dir);
        case Operation.Cards: return new CardsParams(dir);
        case Operation.Flip: return new FlipParams(terms);
        case Operation.Split: return new SplitParams();
        case Operation.Overlay: return new OverlayParams(terms);
        case Operation.Nudge: return new NudgeParams(terms);
        case Operation.HeaderFooter: return new HeaderFooterParams(terms);
        case Operation.ColorBar: return new ColorBarParams(terms);
        case Operation.Nest: return new NestParams();
        case Operation.Calendar: return new CalendarParams(dir);
        case Operation.InsertPages: return new InsertPagesParams(terms);
        case Operation.Gang: return new GangParams();
        case Operation.CutterMarks: return new CutterMarksParams(terms);
        case Operation.MaskingTape: return new MaskingTapeParams(terms);
        case Operation.ToggleLayers: return new ToggleLayersParams();
        case Operation.Expert: return new ExpertParams();
        case Operation.MakeBleeds: return new MakeBleedsParams(terms);
    }
}

export function paramsFrom(op: OpTool, units: Units,
    pageDimensions: PageDimensions[], terms: PageRangeTerms, dir: Dir): ToolParams {
    const params = newParams(op.op, terms, dir);
    try {
        if (params.kind == Operation.Monkey && pageDimensions.length > 0) {
            // The first time the user chooses Monkey, show them a layout that looks
            // like the thumbnail.
            let xwidth = pageDimensions[0].widthPts;
            let xheight = pageDimensions[0].heightPts;
            // Use portrait dimensions.
            if (xheight < xwidth) {
                [xheight, xwidth] = [xwidth, xheight];
            }
            params.paperHeight.text = (
                (0.6 * 72) + xheight + xwidth
            ).toString();
            params.paperWidth.text = (
                (0.6 * 72) + xwidth * 2
            ).toString();
        }
        convertParams(params, Units.POINTS, units);
        if (op.packedParams) {
            unpackParams(params, units, op.packedParams);
        }
    } catch (e) {
       // Catch the error so the user can use the default values if we were
      // unable to unpack whatever values they had stored.
      Bugsnag.notify(toNotifiableError(e));       
    }
    return params;
}

/// Confirms the steps being imported are all of the right types and don't contain
/// unexpected values.
export function validatePackedStep(packedStep: Record<string, any>,
    terms: PageRangeTerms, dir: Dir): void
{
    if (Object.values(Operation).includes(packedStep.kind)) {
        // Collect the names and types we expect to find in packedSteps.
        const params = newParams(packedStep.kind, terms, dir);
        const packedParams = pack(params, Units.POINTS);
        const packedProps = new Set<string>();
        for (const [key, value] of Object.entries(packedParams)) {
            packedProps.add(`${key}: ${typeof value}`);
        }
        // Compare the names and types in packedSteps.
        for (const [key, value] of Object.entries(packedStep)) {
            if (!packedProps.has(`${key}: ${typeof value}`)) {
                throw new Error(`${packedStep.kind} has unexpected value ${key}: ${typeof value}`)
            }
        }
    } else {
        throw new Error(`Unknown step ${packedStep.kind}`);
    }
}

export const PDFSNAKE_VALUES = "PDFSnakeValues";

export function parseStepsJson(buffer: ArrayBuffer | Uint8Array,
    terms: PageRangeTerms, dir: Dir, unitsBox: UnitsBox): [any, ToolParams[]] | [null, null]
{
  const text = new TextDecoder("utf-8").decode(buffer);
  const json = JSON.parse(text);
  const steps: ToolParams[] = [];
  let jsonSteps;
  if (json.kind === PDFSNAKE_VALUES) {
    // It's a steps json intended for CLI or API.
    jsonSteps = json.steps;
  } else if (Array.isArray(json)) {
    // It's old steps, or json that was embedded into the PDF.
    jsonSteps = json; 
  } else {
      return [null, null];
  }
  for (const step of jsonSteps) {
      const params = paramsFromValues(step, terms, dir);
      convertParams(params, Units.POINTS, unitsBox.units);
      steps.push(params);
  }
  return [json, steps];
}